// src/components/songEdit/components/SongSearchAutocomplete.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { FileIndicator } from '../../common/FileIndicator';
import { songEditService } from '../../../services/songEditService';
import { useDebounce } from '../hooks/useDebounce';
import { useClickOutside } from '../hooks/useClickOutside';
import '../styles/songSearch.scss';

interface Version {
 version_id: number;
 version_title: string;
 song_id: number;
}

interface SongSearchResult {
 song_id: number;
 title: string;
 composer_name?: string;
 lyricist_name?: string;
 has_file?: boolean;
 versions?: Version[];
}

interface SongSearchAutocompleteProps {
 onSongSelect: (songId: number, versionId?: number) => void;
}

// In SongSearchAutocomplete.tsx

export const SongSearchAutocomplete: React.FC<SongSearchAutocompleteProps> = ({ onSongSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SongSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(true); // Add this flag
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useClickOutside(searchRef, () => {
    setShowDropdown(false);
  });

  useEffect(() => {
    if (!isSearching) return; // Skip the search if we're not in searching mode

    const searchSongs = async () => {
      if (debouncedSearchTerm.trim().length < 2) {
        setResults([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await songEditService.searchSongs(debouncedSearchTerm);
        setResults(response.songs);
        setShowDropdown(true);
      } catch (error) {
        console.error('Error searching songs:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    searchSongs();
  }, [debouncedSearchTerm, isSearching]);

  const handleSelect = (songId: number, versionId?: number) => {
    console.log('SongSearchAutocomplete - handleSelect called with:', { songId, versionId });
    
    const song = results.find(s => s.song_id === songId);
    if (song) {
      console.log('SongSearchAutocomplete - Found song:', song);
      setIsSearching(false);
      setSearchTerm(song.title);
    }
    setShowDropdown(false);
    setResults([]);
    inputRef.current?.blur();
    
    console.log('SongSearchAutocomplete - Calling onSongSelect');
    onSongSelect(songId, versionId);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsSearching(true); // Enable searching mode when user types
    setSearchTerm(e.target.value);
  };

  return (
    <div className="song-search__container" ref={searchRef}>
      <div className="song-search__input-wrapper">
        <div className="input-group">
          <input
            ref={inputRef}
            type="text"
            className="song-form__input form-control"
            placeholder="Search songs by title..."
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={() => isSearching && setShowDropdown(true)} // Only show dropdown if in searching mode
          />
          <button className="btn btn-icon btn-secondary" type="button">
            <Search size={20} />
          </button>
        </div>
        
        {/* Rest of the component remains the same */}
       
       {showDropdown && (
         <div className="song-search__dropdown">
           {isLoading ? (
             <div className="song-search__loading">
               <div className="song-search__loading-spinner"></div>
               Searching...
             </div>
           ) : results.length > 0 ? (
             <ul className="song-search__list">
               {results.map((song) => (
                 <React.Fragment key={song.song_id}>
                   <li 
                     className="song-search__item song-search__item--main"
                     onClick={() => handleSelect(song.song_id)}
                   >
                     <div className="song-search__item-content">
                       <span className="song-search__title"><FileIndicator hasFile={song.has_file} />{song.title}</span>
                       {song.composer_name && (
                         <span className="song-search__composer">{song.composer_name}</span>
                       )}
                     </div>
                   </li>
                   {song.versions && song.versions.length > 0 && 
                     song.versions.map((version) => (
                       <li
                         key={version.version_id}
                         className="song-search__item song-search__item--version"
                         onClick={() => handleSelect(song.song_id, version.version_id)}
                       >
                         <div className="song-search__item-content">
                           {version.version_title}
                         </div>
                       </li>
                     ))}
                 </React.Fragment>
               ))}
             </ul>
           ) : searchTerm.length >= 2 ? (
             <div className="song-search__no-results">
               No songs found
             </div>
           ) : null}
         </div>
       )}
     </div>
   </div>
 );
};